import { ReactNode } from 'react';
import { Icon, IconName } from '@frontend/icons';
import { Text } from '../text/text.component';
import { Truncated } from '../truncated';
import * as styles from './inline-notification.styles';

type BaseProps = {
  status: 'success' | 'warn' | 'error' | 'info';
  size?: 'large' | 'medium' | 'small';
  maxWidth?: string;
};

type PropsMessage = {
  message: ReactNode;
  children?: never;
};

type PropsChildren = {
  message?: never;
  children: ReactNode;
};

type Props = (BaseProps & PropsMessage) | (BaseProps & PropsChildren);

export type InlineNotificationProps = Props & Omit<React.ComponentPropsWithoutRef<'section'>, keyof Props>;

type IconSelectionProps = Record<InlineNotificationProps['status'], IconName>;

const IconSelection: IconSelectionProps = {
  success: 'success-filled',
  warn: 'alert-filled',
  error: 'error-filled',
  info: 'info-filled',
};

const iconSize = {
  large: 24,
  medium: 24,
  small: 16,
} as const;

export const InlineNotification = ({
  size = 'large',
  message,
  status,
  children,
  maxWidth,
  ...rest
}: InlineNotificationProps) => {
  const iconName = IconSelection[status];

  return (
    <section css={styles.containerStyles({ status, size, maxWidth })} {...rest}>
      <div css={styles.contentContainerStyles({ status, size, maxWidth })}>
        <Icon size={iconSize[size]} name={iconName} />
        {typeof message === 'string' ? (
          size === 'large' ? (
            <Text style={{ margin: 0 }} size={'medium'}>
              {message}
            </Text>
          ) : (
            <Truncated as={Text} style={{ margin: 0 }} size={size === 'small' ? 'small' : 'medium'}>
              {message}
            </Truncated>
          )
        ) : (
          children
        )}
      </div>
    </section>
  );
};
